/**
 * Bearer-token-authenticated client for the email-relay Worker.
 *
 * Worker URL + token live in localStorage so the dashboard can be deployed
 * to Cloudflare Pages without a build-time secret. The configure screen
 * (`SettingsGate`) is the only place that writes them.
 */
import type {
  AccountListResponse,
  AccountRecord,
  CommentCreateResponse,
  CommentListResponse,
  CommentTask,
  ProxiesListResponse,
  ProxyRow,
  ProxyScanResponse,
  RunsListResponse,
  TriggersListResponse,
} from './types';

const URL_KEY = 'auto_reddit_relay_url';
const TOKEN_KEY = 'auto_reddit_relay_token';

export interface RelayConfig {
  url: string;
  token: string;
}

export function loadRelayConfig(): RelayConfig | null {
  const url = localStorage.getItem(URL_KEY);
  const token = localStorage.getItem(TOKEN_KEY);
  if (!url || !token) return null;
  return { url: url.replace(/\/$/, ''), token };
}

export function saveRelayConfig(cfg: RelayConfig): void {
  localStorage.setItem(URL_KEY, cfg.url.replace(/\/$/, ''));
  localStorage.setItem(TOKEN_KEY, cfg.token);
}

export function clearRelayConfig(): void {
  localStorage.removeItem(URL_KEY);
  localStorage.removeItem(TOKEN_KEY);
}

export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

async function request<T>(
  cfg: RelayConfig,
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const res = await fetch(`${cfg.url}${path}`, {
    ...init,
    headers: {
      authorization: `Bearer ${cfg.token}`,
      ...(init.body ? { 'content-type': 'application/json' } : {}),
      ...(init.headers ?? {}),
    },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new ApiError(
      `${init.method ?? 'GET'} ${path} -> ${res.status} ${text.slice(0, 160)}`,
      res.status,
    );
  }
  // Empty bodies on 200 / 204 are valid for some routes.
  const ct = res.headers.get('content-type') ?? '';
  if (!ct.includes('application/json')) {
    return undefined as T;
  }
  return (await res.json()) as T;
}

export async function listAccountUsernames(
  cfg: RelayConfig,
): Promise<AccountListResponse> {
  return request<AccountListResponse>(cfg, '/accounts');
}

export async function fetchAccount(
  cfg: RelayConfig,
  username: string,
): Promise<AccountRecord | null> {
  const r = await request<AccountRecord | Record<string, never>>(
    cfg,
    `/accounts/${encodeURIComponent(username)}`,
  );
  if (!r || Object.keys(r).length === 0) return null;
  return r as AccountRecord;
}

export async function putAccount(
  cfg: RelayConfig,
  username: string,
  record: Partial<AccountRecord>,
): Promise<void> {
  await request<{ ok: true }>(cfg, `/accounts/${encodeURIComponent(username)}`, {
    method: 'PUT',
    body: JSON.stringify(record),
  });
}

/**
 * DELETE the account from KV. The Worker also clears `last_assigned_username`
 * on the matching D1 proxies row so the proxy slot becomes immediately
 * reservable again — see email-relay/src/accounts.ts (handleAccounts).
 */
export async function deleteAccount(
  cfg: RelayConfig,
  username: string,
): Promise<void> {
  await request<{ deleted: true }>(
    cfg,
    `/accounts/${encodeURIComponent(username)}`,
    { method: 'DELETE' },
  );
}

/**
 * Probe every active proxy (or one specific id) and persist the result
 * on each row's `last_check_*` columns. Runs server-side via the Worker;
 * synchronous from the caller's POV. The full sweep takes ~1-2s on a
 * healthy pool — Cloudflare's Workers Sockets layer dials each proxy
 * in parallel.
 */
export async function scanProxies(
  cfg: RelayConfig,
  opts: { id?: string } = {},
): Promise<ProxyScanResponse> {
  return request<ProxyScanResponse>(cfg, '/proxies/scan', {
    method: 'POST',
    body: JSON.stringify(opts),
  });
}

export async function listProxies(cfg: RelayConfig): Promise<ProxyRow[]> {
  const r = await request<ProxiesListResponse>(cfg, '/proxies');
  return r.proxies ?? [];
}

export interface ProxyInput {
  id: string;
  label: string;
  server: string;
  username?: string | null;
  password?: string | null;
  bypass?: string | null;
  status?: 'active' | 'retired';
}

/** POST /proxies — insert-or-replace. Idempotent; the Worker uses
 * INSERT ... ON CONFLICT(id) DO UPDATE so re-submitting with the same id
 * rewrites the row (server / creds / status) without touching
 * last_assigned_at. Use this for both "add a new proxy" and "rotate
 * the credentials on an existing one". */
export async function upsertProxy(
  cfg: RelayConfig,
  input: ProxyInput,
): Promise<void> {
  await request<{ ok: true }>(cfg, '/proxies', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/** DELETE /proxies/<id> — soft-retires by setting status='retired'. The
 * row stays so historical accounts can still resolve their proxy_id back
 * to it. */
export async function retireProxy(
  cfg: RelayConfig,
  id: string,
): Promise<void> {
  await request<{ ok: true }>(cfg, `/proxies/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

/**
 * Push N register tasks onto the register CF Queue. The consumer
 * (`auto-reddit-consumer drain-register` on the host machine) pulls
 * them and runs the full register flow, reserving an eligible proxy
 * per task (1-account-per-proxy-per-day).
 */
export async function enqueueRegisterTasks(
  cfg: RelayConfig,
  count: number,
  reason: string = 'dashboard',
): Promise<{ count: number }> {
  return request<{ count: number }>(cfg, '/queue/register/enqueue', {
    method: 'POST',
    body: JSON.stringify({ count, reason }),
  });
}

export interface ImportProxiesResponse {
  inserted: number;
  skipped: number;
  ids: string[];
  skipped_rows: { line: string; reason: string }[];
}

/** POST /proxies/import — Worker-side fetches the URL (avoids browser
 * CORS), parses Webshare-format lines (host:port:user:pass) and
 * upserts each row keyed on host (so re-importing the same list
 * just refreshes credentials). */
export async function importProxies(
  cfg: RelayConfig,
  opts: { url: string; scheme?: string; label_prefix?: string },
): Promise<ImportProxiesResponse> {
  return request<ImportProxiesResponse>(cfg, '/proxies/import', {
    method: 'POST',
    body: JSON.stringify(opts),
  });
}

/** Pull the operator-curated global subreddit whitelist. The Worker
 * returns `{ subreddits: [] }` when unset, never 404. Used by the
 * Settings page to populate the textarea on load. */
export async function fetchSubredditWhitelist(
  cfg: RelayConfig,
): Promise<string[]> {
  const r = await request<{ subreddits: string[] }>(
    cfg,
    '/settings/subreddit-whitelist',
  );
  return r.subreddits ?? [];
}

/** Replace the global whitelist. Worker normalizes (lowercase, dedup,
 * strip r/ prefix) so the operator's pasted list rarely matches byte-for-
 * byte what comes back on the next GET — refresh after save. */
export async function setSubredditWhitelist(
  cfg: RelayConfig,
  subreddits: string[],
): Promise<{ ok: true; count: number }> {
  return request<{ ok: true; count: number }>(
    cfg,
    '/settings/subreddit-whitelist',
    {
      method: 'PUT',
      body: JSON.stringify({ subreddits }),
    },
  );
}

export async function postWarmTriggers(
  cfg: RelayConfig,
  usernames: string[],
): Promise<{ created: number }> {
  return request<{ created: number }>(cfg, '/warm/triggers', {
    method: 'POST',
    body: JSON.stringify({ usernames }),
  });
}

/**
 * Push one warm task per username to the Cloudflare Queue.
 *
 * Replaces the older `postWarmTriggers` flow that wrote to the KV
 * `/warm/triggers` namespace (consumed only by the legacy
 * `auto-reddit warm-pool --triggers-only` cron). The producer/consumer
 * split uses the CF Queue end-to-end, so this is the route the consumer
 * (`auto-reddit-consumer drain`) actually reads from.
 *
 * Loops on the client since the Worker's enqueue endpoint takes one
 * message at a time. With dashboards usually triggering ≤20 accounts at
 * a time this is fine; if we need bulk later, add a batch endpoint.
 */
export async function enqueueWarmTasks(
  cfg: RelayConfig,
  usernames: string[],
  reason: string = 'dashboard',
): Promise<{ created: number; failed: string[] }> {
  let created = 0;
  const failed: string[] = [];
  for (const username of usernames) {
    try {
      await request<{ ok: true }>(cfg, '/queue/warm/enqueue', {
        method: 'POST',
        body: JSON.stringify({ username, reason }),
      });
      created++;
    } catch (e) {
      failed.push(`${username}: ${e instanceof Error ? e.message : String(e)}`);
    }
  }
  return { created, failed };
}

export async function listWarmTriggers(
  cfg: RelayConfig,
): Promise<TriggersListResponse> {
  return request<TriggersListResponse>(cfg, '/warm/triggers');
}

export async function listWarmRuns(
  cfg: RelayConfig,
  opts: { username?: string; limit?: number } = {},
): Promise<RunsListResponse> {
  const params = new URLSearchParams();
  if (opts.username) params.set('username', opts.username);
  params.set('limit', String(opts.limit ?? 20));
  return request<RunsListResponse>(cfg, `/warm/runs?${params.toString()}`);
}

/**
 * Pull the full account roster. The list endpoint is cheap (KV metadata only),
 * but the dashboard needs full records for the table view, so we fan out.
 * For 200-500 accounts this is hundreds of GETs in parallel — fine for a
 * dashboard refresh button, ill-advised on every render.
 */
export async function fetchAllAccounts(cfg: RelayConfig): Promise<AccountRecord[]> {
  const list = await listAccountUsernames(cfg);
  const records = await Promise.all(
    list.usernames.map((u) =>
      fetchAccount(cfg, u).catch(() => null),
    ),
  );
  return records.filter((r): r is AccountRecord => r !== null);
}

// ---- Comment tasks ----

/** Submit a comment task (account posts `body` on the Reddit post at `url`).
 * The consumer's `drain-comments` picks it up and posts it asynchronously. */
export async function createComment(
  cfg: RelayConfig,
  input: { account: string; url: string; body: string },
): Promise<CommentTask> {
  const r = await request<CommentCreateResponse>(cfg, '/comments', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return r.task;
}

/** Recent comment tasks, newest first, for the status list. */
export async function listComments(
  cfg: RelayConfig,
  limit = 50,
): Promise<CommentTask[]> {
  const r = await request<CommentListResponse>(cfg, `/comments?limit=${limit}`);
  return r.comments ?? [];
}
